/**
 * Supplier Returns Page
 *
 * List page for supplier returns with filters, table, and drawer.
 * MANAGER+ can create/edit (with backdating limits), OWNER unlimited.
 * @see US-40.3 - Frontend retours fournisseur
 */

import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Undo2, Plus, ChevronDown, FileText, Package } from 'lucide-react';
import { fetchAllForExport } from '@bill/ui';
import { EmptyState, Select, Button } from '@/components/ui';
import { Dropdown, DropdownItem } from '@/components/ui';
import { DatePicker, formatDateParam, parseDateParam } from '@/components/ui';
import type { DateRange } from '@/components/ui';
import { useDrawerRoute, usePermission, useCanSeeFinancials } from '@/hooks';
import {
  useSupplierReturns,
  useValidateSupplierReturn,
  useCancelSupplierReturn,
  useDeleteSupplierReturn,
} from '../hooks/useSupplierReturns';
import { supplierReturnsService } from '../services/supplier-returns.service';
import { SupplierReturnDrawer } from '../components/SupplierReturnDrawer';
import { SupplierReturnFormModal } from '../components/SupplierReturnFormModal';
import { SelectReceiptModal } from '../components/SelectReceiptModal';
import { SupplierReturnsTable } from '../components/SupplierReturnsTable';
import { SupplierReturnStatus } from '@bstock/shared';
import type { SupplierReturn, QuerySupplierReturnsDto } from '@bstock/shared';

const DEFAULT_LIMIT = 20;

function getInitialDateRange(): DateRange {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

function parseUrlParams(searchParams: URLSearchParams) {
  const startDate = searchParams.get('dateFrom');
  const endDate = searchParams.get('dateTo');
  const defaultRange = getInitialDateRange();

  return {
    status: searchParams.get('status') ?? 'ALL',
    supplierId: searchParams.get('supplierId') ?? '',
    dateRange: {
      start: startDate ? parseDateParam(startDate) : defaultRange.start,
      end: endDate ? parseDateParam(endDate) : defaultRange.end,
    } as DateRange,
    page: parseInt(searchParams.get('page') ?? '1', 10),
    sortBy: searchParams.get('sortBy') ?? 'returnDate',
    sortOrder: searchParams.get('sortOrder') ?? 'desc',
    limit: parseInt(searchParams.get('limit') ?? String(DEFAULT_LIMIT), 10),
  };
}

export default function SupplierReturnsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Permissions
  const canCreate = usePermission('supplier-returns.create');
  const canValidate = usePermission('supplier-returns.validate');
  const canDelete = usePermission('supplier-returns.delete');
  const showFinancials = useCanSeeFinancials();

  // Parse URL params for deep linking
  const urlParams = useMemo(() => parseUrlParams(searchParams), [searchParams]);

  // Drawer routing
  const drawer = useDrawerRoute('/achats/retours-fournisseur', { paramName: 'returnId' });

  // Filters
  const [dateRange, setDateRange] = useState<DateRange>(urlParams.dateRange);
  const [statusFilter, setStatusFilter] = useState(urlParams.status);
  const [page, setPage] = useState(urlParams.page);
  const [limit, setLimit] = useState(urlParams.limit);
  const [sortBy, setSortBy] = useState(urlParams.sortBy);
  const [sortOrder, setSortOrder] = useState(urlParams.sortOrder);

  // Form modal
  const [showSelectReceiptModal, setShowSelectReceiptModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editReturnId, setEditReturnId] = useState<string | null>(null);
  const [selectedReceiptId, setSelectedReceiptId] = useState<string | null>(null);

  // Update URL when filters change
  const updateUrl = useCallback(
    (updates: Record<string, string | null>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Query params
  const queryParams = useMemo((): QuerySupplierReturnsDto => ({
    page,
    limit,
    status: statusFilter !== 'ALL' ? statusFilter as SupplierReturnStatus : undefined,
    dateFrom: formatDateParam(dateRange.start) ?? undefined,
    dateTo: formatDateParam(dateRange.end) ?? undefined,
  }), [page, limit, statusFilter, dateRange]);

  // Fetch returns
  const { data: returnsData, isLoading } = useSupplierReturns(queryParams);

  // Mutations
  const validateMutation = useValidateSupplierReturn();
  const cancelMutation = useCancelSupplierReturn();
  const deleteMutation = useDeleteSupplierReturn();

  const returns = returnsData?.data ?? [];
  const pagination = returnsData?.pagination ?? { page: 1, limit, total: 0, totalPages: 0 };

  // Handlers
  const handleRowClick = useCallback(
    (ret: SupplierReturn) => {
      drawer.open(ret.id);
    },
    [drawer]
  );

  const handleDateRangeChange = useCallback(
    (range: DateRange) => {
      setDateRange(range);
      setPage(1);
      updateUrl({
        dateFrom: formatDateParam(range.start),
        dateTo: formatDateParam(range.end),
        page: null,
      });
    },
    [updateUrl]
  );

  const handleStatusChange = useCallback(
    (status: string) => {
      setStatusFilter(status);
      setPage(1);
      updateUrl({ status: status !== 'ALL' ? status : null, page: null });
    },
    [updateUrl]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      updateUrl({ page: newPage > 1 ? String(newPage) : null });
    },
    [updateUrl]
  );

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      setLimit(newLimit);
      setPage(1);
      updateUrl({ limit: String(newLimit), page: null });
    },
    [updateUrl]
  );

  const handleSort = useCallback(
    (field: string, order: string) => {
      setSortBy(field);
      setSortOrder(order);
      setPage(1);
      updateUrl({ sortBy: field, sortOrder: order, page: null });
    },
    [updateUrl]
  );

  const handleValidate = useCallback(
    (ret: SupplierReturn) => {
      if (confirm('Valider ce retour ? Les mouvements de stock seront crees.')) {
        validateMutation.mutate(ret.id);
      }
    },
    [validateMutation]
  );

  const handleCancel = useCallback(
    (ret: SupplierReturn) => {
      if (confirm('Annuler ce retour ? Des contre-mouvements seront crees.')) {
        cancelMutation.mutate({ id: ret.id });
      }
    },
    [cancelMutation]
  );

  const handleDelete = useCallback(
    (ret: SupplierReturn) => {
      if (confirm('Supprimer ce retour brouillon ?')) {
        deleteMutation.mutate(ret.id);
      }
    },
    [deleteMutation]
  );

  const handleEdit = useCallback(
    (ret: SupplierReturn) => {
      setEditReturnId(ret.id);
      setSelectedReceiptId(null);
      setShowFormModal(true);
    },
    []
  );

  const statusOptions = [
    { value: 'ALL', label: 'Tous les statuts' },
    { value: 'DRAFT', label: 'Brouillon' },
    { value: 'VALIDATED', label: 'Valide' },
    { value: 'CANCELLED', label: 'Annule' },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Undo2 className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-dark-500">Retours fournisseur</h1>
              <p className="text-sm text-neutral-500 mt-0.5">
                Gestion des retours de marchandise vers les fournisseurs
              </p>
            </div>
          </div>
          {canCreate && (
            <Dropdown
              trigger={
                <Button>
                  <Plus className="w-4 h-4" />
                  Nouveau retour
                  <ChevronDown className="w-4 h-4" />
                </Button>
              }
              align="right"
            >
              <DropdownItem icon={<FileText className="w-4 h-4" />} onClick={() => setShowSelectReceiptModal(true)}>
                A partir d'une reception
              </DropdownItem>
              <DropdownItem icon={<Package className="w-4 h-4" />} onClick={() => {
                setEditReturnId(null);
                setSelectedReceiptId(null);
                setShowFormModal(true);
              }}>
                Retour libre
              </DropdownItem>
            </Dropdown>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-neutral-200 px-6 py-3 flex-shrink-0">
        <div className="flex items-end gap-4 flex-wrap">
          <div className="w-72">
            <label className="block text-sm font-medium text-dark-500 mb-1.5">Periode</label>
            <DatePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              placeholder="Selectionner une periode"
            />
          </div>
          <div className="w-44">
            <Select
              label="Statut"
              value={statusFilter}
              onChange={handleStatusChange}
              options={statusOptions}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 bg-neutral-50">
        {returns.length === 0 && !isLoading ? (
          <EmptyState
            icon={Undo2}
            title="Aucun retour fournisseur"
            description="Les retours de marchandise vers vos fournisseurs apparaitront ici."
          />
        ) : (
          <SupplierReturnsTable
            returns={returns}
            pagination={pagination}
            isLoading={isLoading}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onRowClick={handleRowClick}
            onValidate={canValidate ? handleValidate : undefined}
            onCancel={canValidate ? handleCancel : undefined}
            onDelete={canDelete ? handleDelete : undefined}
            onEdit={canCreate ? handleEdit : undefined}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            onFetchAllRows={() =>
              fetchAllForExport(async ({ limit: exportLimit }) => {
                const response = await supplierReturnsService.list({
                  ...queryParams,
                  page: 1,
                  limit: exportLimit,
                });
                return {
                  items: response.data,
                  pagination: { total: response.pagination.total },
                };
              })
            }
            showFinancials={showFinancials}
          />
        )}
      </div>

      {/* Drawer */}
      <SupplierReturnDrawer
        returnId={drawer.resourceId}
        open={drawer.isOpen}
        onOpenChange={(open) => { if (!open) drawer.close(); }}
        showFinancials={showFinancials}
        canValidate={canValidate}
        canDelete={canDelete}
        onEdit={canCreate ? (id: string) => {
          setEditReturnId(id);
          setSelectedReceiptId(null);
          setShowFormModal(true);
        } : undefined}
      />

      {/* Select Receipt Modal */}
      <SelectReceiptModal
        isOpen={showSelectReceiptModal}
        onClose={() => setShowSelectReceiptModal(false)}
        onSelect={(receiptId) => {
          setSelectedReceiptId(receiptId);
          setEditReturnId(null);
          setShowSelectReceiptModal(false);
          setShowFormModal(true);
        }}
      />

      {/* Form Modal */}
      {showFormModal && (
        <SupplierReturnFormModal
          returnId={editReturnId}
          receiptId={selectedReceiptId}
          onClose={() => {
            setShowFormModal(false);
            setEditReturnId(null);
            setSelectedReceiptId(null);
          }}
        />
      )}
    </div>
  );
}
